import React, { FC, ChangeEvent, useState } from 'react';
import { Paper } from '@material-ui/core';
import { pull, uniq } from 'lodash';

import { SearchSortDirections, SearchSortFields, useBatchDeleteGroupsMutation } from 'api';
import { useBatchDeleteGroups } from 'features/admin/updaters';
import { EmptyState } from 'components';
import { useNotify, useGroupSearch } from 'hooks';

import Header from '../components/Groups/Header';
import Table from '../components/Groups/Table';

const RESULTS_FROM = 0;
const SIZE = 24;

const Groups: FC = () => {
  const [checkedIds, setCheckedIds] = useState([]);
  const [allChecked, setAllChecked] = useState(false);
  const notify = useNotify();

  const variables = {
    results_from: RESULTS_FROM,
    size: SIZE,
    auto_exclude: false,
    sort: SearchSortFields.Title,
    sort_direction: SearchSortDirections.Asc,
  };

  const mutationOptions = useBatchDeleteGroups(variables);
  const { count, results, sentinelRef, isEmpty, isExhausted, isInitialLoad } =
    useGroupSearch(variables);

  const { mutate: batchDeleteAdminGroups } = useBatchDeleteGroupsMutation({
    onSuccess: handleMutationCompleted,
    ...mutationOptions,
  });

  function handleChecked(event: ChangeEvent<HTMLInputElement>) {
    const { checked, name } = event.target;

    checked ? checkedIds.unshift(name) : pull(checkedIds, name);
    setCheckedIds(uniq(checkedIds));
  }

  function handleAllChecked(event: ChangeEvent<HTMLInputElement>) {
    const { checked } = event.target;
    const ids = checked ? results.map(result => result.id) : [];

    setCheckedIds(ids);
    setAllChecked(checked);
  }

  function handleMutationCompleted() {
    notify.info({
      i18n: {
        i18nKey: 'admin:deleteGroupSuccessMessage',
        count: checkedIds.length,
        values: { checkedTotal: checkedIds.length },
      },
    });

    setCheckedIds([]);
  }

  function handleAction() {
    setAllChecked(false);
    batchDeleteAdminGroups({ ids: checkedIds });
  }

  function renderTable() {
    if (isEmpty) {
      return <EmptyState name="groupsOwned" />;
    }

    return (
      <Table
        results={results}
        sentinelRef={sentinelRef}
        isInitialLoad={isInitialLoad}
        isAllChecked={allChecked}
        isExhausted={isExhausted}
        checkedIds={checkedIds}
        onAllChecked={handleAllChecked}
        onChecked={handleChecked}
      />
    );
  }

  return (
    <Paper>
      <Header total={count} checkedTotal={checkedIds.length} onAction={handleAction} />
      {renderTable()}
    </Paper>
  );
};

export default Groups;
